import { useCallback, useEffect, useRef, useState } from 'react'
import { useError } from '@/hooks/useError'
import {
  getHazmat,
  type Hazmat,
  type HazmatTarget,
  hazmatTargetKey,
} from '../lib/hazmatTarget'

/**
 * Loads a target's Hazmat determination while enabled. reload refreshes it in place, without
 * going back to the loading state, so the UI showing it stays mounted.
 */
export function useHazmat(target: HazmatTarget, enabled = true) {
  const { showError } = useError()
  const [hazmat, setHazmat] = useState<Hazmat | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const targetKey = hazmatTargetKey(target)
  // Callers usually build the target inline, so read it through a ref and key effects on targetKey
  const targetRef = useRef(target)
  targetRef.current = target

  const fetchHazmat = useCallback(async () => {
    try {
      const response = await getHazmat(targetRef.current)
      setHazmat(response)
    } catch (e) {
      showError(e, 'Failed to load hazmat information.')
    }
  }, [showError])

  useEffect(() => {
    if (!enabled) return
    setIsLoading(true)
    fetchHazmat().finally(() => setIsLoading(false))
  }, [enabled, targetKey, fetchHazmat])

  return { hazmat, isLoading, reload: fetchHazmat }
}
